import React, { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { Container } from '@johnlewispartnership/wtr-ingredients/dist/foundations/grid';
import { useMediaQuery } from 'react-responsive';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { BREAKPOINTS } from 'constants/grid';
import TopNav from 'components/SiteHeaderNew/TopNav';
import HeaderLogo from 'components/HeaderLogo';
import { useScrolled } from 'hooks/use-scrolled';
import SearchForm from 'components/Search/SearchForm';
import SkipLink from 'components/SkipLink';
import { stickyHeaderOffset } from 'constants/siteHeader';
import { thirtyDaysInMilliseconds } from 'constants/cookies';
import { usePreloadedData } from 'contexts/PreloadedData';
import contentService from 'services/content';
import useMonetateReporter from 'hooks/use-monetate-reporter';
import { useSlotContext } from 'contexts/Slot';
import { deleteCookie, setCookie } from 'utils/cookie';
import SitePinBar from 'components/SitePinBar';
import AppBanner from 'components/AppBanner';
import SlideOutNav from './SlideOutNav';
import styles from './SiteHeader.module.scss';
import DropdownNav from './LinksBar/DropdownNav';
import DropdownNavHidden from './LinksBar/DropdownNavHidden';
import LinksBar from './LinksBar';
import MenuLinksBar from './MenuLinksBar';

const fetchContent = async (experienceFragmentKey, hasTokenSession) =>
  contentService.getContent(experienceFragmentKey, hasTokenSession);

const SiteHeader = () => {
  const { state: authState } = useAuthentication();

  useMonetateReporter();

  const [isTablet, setIsTablet] = useState(false);

  const [isSeasonalMenuOpen, setIsSeasonalMenuOpen] = useState(false);

  const ref = useRef();
  const { scrolled } = useScrolled(stickyHeaderOffset);
  const placeholderStyle = useMemo(() => {
    const { current: el } = ref;
    if (!scrolled || !el) return { display: 'none' };
    return { height: el.getBoundingClientRect().height };
  }, [scrolled]);

  // DropdownNav is rendered at a different position depending on whether it
  // is sticky or not so that the tabbing order is correct alongside search form/header buttons
  const menu = (
    <div className={styles.menu} key="desktopMenu">
      <DropdownNav scrolled={scrolled} />
    </div>
  );

  const isTabletBreakpoint = useMediaQuery({ minWidth: BREAKPOINTS.md, maxWidth: BREAKPOINTS.lg });
  const isDesktop = useMediaQuery({ minWidth: BREAKPOINTS.lg });
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINTS.md });

  useEffect(() => {
    if (authState.initialised) {
      if (authState.isLoggedIn) {
        // Set customerId cookie for server-side reporting and third-party integrations
        setCookie('customerId', authState.customerId, {
          secure: true,
          maxAge: thirtyDaysInMilliseconds,
        });
      } else {
        deleteCookie('customerId');

        if (authState.hasTokenSession) {
          // Server-side rendered the logged-in header but now we've done the auth flow
          // client-side and they're not actually logged in, so it'll re-render logged-out.
          // This is expected e.g. when token expires, but we log it so we can track/analyse.
          const errorToSendToApm = new Error('Re-rendering header from logged-in to logged-out');
          window?.apm?.captureError(
            Object.assign(errorToSendToApm, {
              event: 'header_flicker',
              authError: authState.error?.message,
            }),
          );
        }
      }
    }
  }, [
    authState.customerId,
    authState.error,
    authState.hasTokenSession,
    authState.initialised,
    authState.isLoggedIn,
  ]);

  useEffect(() => {
    setIsTablet(isTabletBreakpoint);
  }, [isTabletBreakpoint]);

  const { preloadedData, setPreloadedData } = usePreloadedData();

  useEffect(() => {
    if (
      typeof preloadedData?.middleMegaMenu === 'undefined' ||
      typeof preloadedData?.seasonalMenu === 'undefined'
    ) {
      const middleMegaMenuContent =
        typeof preloadedData?.middleMegaMenu === 'undefined'
          ? fetchContent('menu/middle-mega-menu/master', authState.hasTokenSession)
          : preloadedData.middleMegaMenu;

      const seasonalMenuContent =
        typeof preloadedData?.seasonalMenu === 'undefined'
          ? fetchContent(
              'menu/seasonal-menu/seasonal-menu-content/master',
              authState.hasTokenSession,
            )
          : preloadedData.seasonalMenu;

      // deepcode ignore PromiseNotCaughtGeneral - SeviceClients have built-in error handling and always resolves
      Promise.allSettled([middleMegaMenuContent, seasonalMenuContent]).then(
        ([middleMegaMenu, seasonalMenu]) => {
          setPreloadedData({
            middleMegaMenu: middleMegaMenu.value,
            seasonalMenu: seasonalMenu.value,
          });
        },
      );
    }
  }, [preloadedData, setPreloadedData, authState.hasTokenSession]);

  const shouldRenderLoggedInVersion =
    (authState.hasTokenSession && !authState.initialised) ||
    (authState.initialised && authState.isLoggedIn);

  const {
    slotContext: { loading: slotLoading, booked: slotBooked, details: slotDetails },
  } = useSlotContext();

  const toggleSeasonalMenu = useCallback(
    () => setIsSeasonalMenuOpen(!isSeasonalMenuOpen),
    [isSeasonalMenuOpen],
  );

  return (
    <>
      {/*
        This style tag avoids a flash of unstyled content.
        These styles are overwritten by the CSS file once loaded.
        See WPIP-72426.
      */}
      <style>
        {`
          .common-mfe-header {
            height: 50px;
            visibility: hidden;

            @media (min-width: 992px) {
              height: 159px;
            }
          }
        `}
      </style>
      <div className={`${styles.header} common-mfe-header`} data-testid="site-header">
        <SkipLink />
        <AppBanner />
        <TopNav />
        <div className={styles.desktop}>
          <DropdownNavHidden />
          {scrolled && <div data-testid="DesktopHeaderPlaceholder" style={placeholderStyle} />}
          <div
            className={classNames(styles.desktopHeader, {
              [styles.scrolled]: scrolled,
            })}
            data-testid="DesktopHeader"
            ref={ref}
          >
            <Container className={classNames(styles.container)}>
              <div className={styles.mobileMenu}>
                <SlideOutNav
                  isSlotBooked={slotBooked}
                  slotDetails={slotDetails}
                  slotLoading={slotLoading}
                  isLoggedIn={shouldRenderLoggedInVersion}
                />
              </div>
              <div className={styles.mobileLogo}>
                <HeaderLogo small staticRender={false} />
              </div>
              <div className={styles.logo}>
                <HeaderLogo staticRender={false} />
              </div>

              {scrolled && menu}

              <div
                className={classNames(styles.inner, { [styles.searchScrolled]: scrolled })}
                data-testid="inner"
              >
                {isMobile && (
                  <div className={styles.links} data-testid="links">
                    <LinksBar
                      isMobile={isMobile}
                      isDesktop={isDesktop}
                      showContactAddressNotPresentNotification={false}
                      currentPageIsGroceriesHomePage={false}
                      isLoggedIn={shouldRenderLoggedInVersion}
                      onToggleSeasonalMenu={toggleSeasonalMenu}
                      isSeasonalMenuOpen={isSeasonalMenuOpen}
                    />
                  </div>
                )}
                <div className={styles.search} data-testid="search">
                  <SearchForm
                    historyClassName={isMobile && styles.mobileSearchHistoryContainer}
                    placeholder={isTablet ? 'Search...' : 'Search groceries...'}
                  />
                </div>

                {!isMobile && (
                  <div className={styles.links} data-testid="links">
                    <LinksBar
                      isMobile={isMobile}
                      isDesktop={isDesktop}
                      showContactAddressNotPresentNotification={false}
                      currentPageIsGroceriesHomePage={false}
                      isLoggedIn={shouldRenderLoggedInVersion}
                      onToggleSeasonalMenu={toggleSeasonalMenu}
                      isSeasonalMenuOpen={isSeasonalMenuOpen}
                    />
                  </div>
                )}
              </div>

              {!scrolled && menu}
              {isDesktop && !scrolled && (
                <div className={styles.links}>
                  <MenuLinksBar
                    onToggleSeasonalMenu={toggleSeasonalMenu}
                    isSeasonalMenuOpen={isSeasonalMenuOpen}
                  />
                </div>
              )}
            </Container>
          </div>
        </div>
        <SitePinBar />
      </div>
    </>
  );
};

export { SiteHeader };
